// Compresión/redimensión de imágenes en el navegador (Canvas nativo, sin
// dependencias) para adjuntos de imagen.
//
// Motivación: el usuario adjunta una foto de celular (p. ej. iPhone, 2–5 MB) y
// el backend/nginx la rechaza por tamaño (413) ANTES de llegar a Django, lo que
// se presenta como un "soft fail". Reducimos la imagen en el cliente para que
// el payload quede holgadamente por debajo del límite del servidor.
//
// Efecto secundario útil: al re-dibujar sobre un canvas, normalizamos cualquier
// formato que el navegador SÍ pueda decodificar (incl. HEIC en iOS Safari) a un
// formato estándar. Si el navegador no puede decodificar el archivo (p. ej.
// HEIC en Chrome de escritorio), devolvemos el original intacto y dejamos que
// el llamador aplique su tope de tamaño.

const DEFAULT_MAX_EDGE = 1600;      // borde más largo permitido, en px
const DEFAULT_MAX_BYTES = 1000000;  // objetivo ~1 MB (bajo el límite del backend/nginx)
const START_QUALITY = 0.8;
const MIN_QUALITY = 0.5;
const QUALITY_STEP = 0.15;

// Tipos que pueden llevar transparencia: en modo 'auto' se conservan como PNG
// para no aplastar el canal alfa (p. ej. logos). Los comprobantes (fotos y
// capturas, opacos) usan el modo 'jpeg' por defecto, que comprime mucho mejor.
const ALPHA_TYPES = ['image/png', 'image/webp', 'image/gif'];

/**
 * Devuelve un File de imagen reducido a partir de `file`. Nunca lanza: ante
 * cualquier problema (no es imagen, el navegador no la decodifica, toBlob falla)
 * devuelve el archivo original para no bloquear el flujo del usuario.
 *
 * @param {File} file
 * @param {{maxEdge?: number, maxBytes?: number, minQuality?: number,
 *          format?: 'jpeg'|'auto'}} [opts]
 *   - format 'jpeg' (default): siempre re-codifica a JPEG con fondo blanco.
 *     Ideal para comprobantes (opacos), máxima compresión.
 *   - format 'auto': conserva PNG cuando el origen puede tener transparencia
 *     (logos); JPEG en cualquier otro caso.
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
    const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE;
    const maxBytes = opts.maxBytes || DEFAULT_MAX_BYTES;
    const minQuality = opts.minQuality || MIN_QUALITY;
    const format = opts.format || 'jpeg';

    // No es una imagen que podamos procesar → tal cual.
    if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        return file;
    }

    const asPng = format === 'auto' && ALPHA_TYPES.indexOf(file.type) !== -1;
    const outType = asPng ? 'image/png' : 'image/jpeg';

    let bitmap;
    try {
        bitmap = await loadBitmap(file);
    } catch (e) {
        // El navegador no pudo decodificar el archivo (formato no soportado).
        return file;
    }

    const target = fitWithin(bitmap.width, bitmap.height, maxEdge);

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        if (bitmap.close) bitmap.close();
        return file;
    }
    // Para JPEG (sin alfa) pintamos fondo blanco: si el origen tuviera zonas
    // transparentes, quedan en blanco y no en negro.
    if (!asPng) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, target.width, target.height);
    }
    ctx.drawImage(bitmap, 0, 0, target.width, target.height);
    if (bitmap.close) bitmap.close();

    let blob;
    if (asPng) {
        // PNG no admite parámetro de calidad; la reducción viene del downscale.
        blob = await canvasToBlob(canvas, outType, undefined);
    } else {
        // JPEG: bajar calidad iterativamente hasta caer bajo maxBytes.
        let quality = START_QUALITY;
        blob = await canvasToBlob(canvas, outType, quality);
        while (blob && blob.size > maxBytes && quality > minQuality) {
            quality = Math.max(minQuality, quality - QUALITY_STEP);
            blob = await canvasToBlob(canvas, outType, quality);
        }
    }

    if (!blob) return file; // toBlob no soportado / falló → original

    return new File([blob], renameTo(file.name, asPng ? 'png' : 'jpg'), {
        type: outType,
        lastModified: Date.now(),
    });
}

// Carga la imagen como bitmap respetando la orientación EXIF (clave para fotos
// de celular, que suelen venir rotadas). Usa createImageBitmap cuando existe y
// cae a <img>+objectURL en navegadores viejos.
function loadBitmap(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
            // Algunos navegadores no aceptan el 2º argumento: reintentar simple.
            return createImageBitmap(file);
        }
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
        img.src = url;
    });
}

function fitWithin(w, h, maxEdge) {
    const longest = Math.max(w, h);
    if (longest <= maxEdge) return { width: w, height: h };
    const scale = maxEdge / longest;
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
        if (!canvas.toBlob) { resolve(null); return; }
        canvas.toBlob((blob) => resolve(blob), type, quality);
    });
}

function renameTo(name, ext) {
    if (!name) return 'imagen.' + ext;
    return name.replace(/\.[^.]+$/, '') + '.' + ext;
}

export const IMAGE_MAX_BYTES = DEFAULT_MAX_BYTES;
